(() => {
  'use strict';
  if (window.BlendContactMeasurement) return;

  const VERSION = '2026-10-12-contact-1';
  const DESTINATION = 'G-C71KQBEKW5';
  const EVENTS = new Set([
    'drawer_open', 'route_view', 'route_select', 'drawer_close', 'drawer_page_exit',
    'drawer_hidden', 'drawer_return', 'form_start', 'step_view', 'step_start',
    'step_complete', 'step_edit', 'step_edit_save', 'step_edit_cancel',
    'validation_error', 'form_reset', 'submit_attempt', 'submit_error', 'submit_success',
    'booking_view', 'booking_action', 'booking_submit', 'booking_error', 'booking_success',
    'navigation', 'open_error', 'landing_view', 'landing_select'
  ].map((name) => `contact_${name}`));
  const ROUTES = new Set(['start', 'form', 'calendar']);
  const STAGES = new Set([
    'welcome', 'name', 'email', 'website', 'growth_goal', 'position', 'sessions',
    'timeline', 'context', 'booking_loading', 'booking_loaded', 'booking_profile', 'booking_date', 'booking_time',
    'booking_details', 'booking_error', 'booking_success', 'enquiry_success', 'other'
  ]);
  const QUESTION_STAGES = Object.freeze({
    growth_objective: 'growth_goal', cro_position: 'position', sessions: 'sessions', timeline: 'timeline'
  });
  const BOOKING_STAGES = Object.freeze({
    loading: 'booking_loading', picker: 'booking_date', times: 'booking_time',
    details: 'booking_details', error: 'booking_error', confirmation: 'booking_success'
  });
  const ENTRIES = new Set(['header', 'footer', 'contact_page', 'cta', 'agent', 'other']);
  const ACTIONS = new Set([
    'open', 'progress', 'route_select', 'back', 'forward', 'next', 'previous', 'reset',
    'close_button', 'escape', 'backdrop', 'programmatic', 'navigation', 'pagehide',
    'bfcache', 'hidden', 'visible', 'return', 'edit', 'save', 'cancel', 'input', 'change',
    'submit', 'retry', 'month_previous', 'month_next', 'timezone_change', 'date_select',
    'time_select', 'change_date', 'change_time', 'reschedule', 'calendar_cancel',
    'fallback', 'agent', 'disconnect', 'success', 'other'
  ]);
  const ERRORS = new Set([
    'required', 'invalid_email', 'invalid_url', 'invalid_choice', 'invalid_value',
    'file_type', 'file_size', 'file_count', 'network', 'http_error', 'timeout',
    'unavailable', 'no_slots', 'slot_unavailable', 'submission_failed', 'booking_failed', 'invalid_response', 'unknown'
  ]);
  const OUTCOMES = new Set(['incomplete', 'enquiry_pending', 'booking_pending', 'enquiry_unknown', 'booking_unknown', 'enquiry_sent', 'booking_complete']);
  const TERMINAL_ERRORS = new Set(['network', 'http_error', 'timeout', 'invalid_response', 'slot_unavailable', 'submission_failed', 'booking_failed']);
  const LIFECYCLE = new Set(['contact_drawer_hidden', 'contact_drawer_return', 'contact_drawer_page_exit']);
  const safeToken = (value, allowed, fallback) => typeof value === 'string' && allowed.has(value) ? value : fallback;
  const now = () => window.performance?.now?.() ?? Date.now();
  // These are visible-page durations, not a claim of continuous keyboard engagement.
  const duration = (value) => Math.round(Math.min(4 * 60 * 60 * 1000, Math.max(0, Number.isFinite(value) ? value : 0)));
  const randomId = () => {
    try { return window.crypto.randomUUID(); }
    catch (_) { return `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`; }
  };

  class BlendContactMeasurement {
    constructor(owner) {
      this.owner = owner;
      this.active = false;
      this.cohort = false;
      this.qa = false;
      this.destroyed = false;
      this.outcome = 'incomplete';
      this.editIndex = null;
      this.seenViews = new Set();
      this.seenRoutes = new Set();
      this.completed = new Set();
      this.validationTimes = new Map();
      this.pendingRequests = new Map();
      this.providerReceipts = new Set();
      this.providerCycle = 0;
      this.iframeStage = 'booking_loading';
      this.landingSelections = new Set();
      this.landingId = randomId();
      this.sequence = 0;
      this.activeMs = 0;
      this.stepMs = 0;
      this.lastActiveAt = null;
      try {
        this.qa = new URL(window.location.href).searchParams.get('contact_tracking_test') === '1';
        const options = owner?.signal ? { signal: owner.signal } : undefined;
        this.onVisibility = () => this.visibilityChanged();
        this.onPageHide = (event) => this.pageHidden(event);
        this.onPageShow = (event) => this.pageShown(event);
        this.onProviderMessage = (event) => this.providerMessage(event);
        this.onConsent = () => this.recordLandingView();
        document.addEventListener('visibilitychange', this.onVisibility, options);
        window.addEventListener('pagehide', this.onPageHide, options);
        window.addEventListener('pageshow', this.onPageShow, options);
        window.addEventListener('message', this.onProviderMessage, options);
        document.addEventListener('visitorConsentCollected', this.onConsent, options);
        this.onDomReady = () => this.setupLanding();
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', this.onDomReady, { ...options, once: true });
        } else this.setupLanding();
        owner?.signal?.addEventListener('abort', () => this.destroy(), { once: true });
      } catch (_) { /* Analytics must never interrupt the drawer. */ }
    }

    canMeasure() {
      try {
        const url = new URL(window.location.href);
        return !this.destroyed && !window.Shopify?.designMode && !window.Shopify?.inspectMode &&
          window.Shopify?.theme?.role === 'main' &&
          ['blendcommerce.com', 'www.blendcommerce.com'].includes(url.hostname.toLowerCase()) &&
          !['preview_theme_id', '_fd', '_sc'].some((key) => url.searchParams.has(key)) &&
          window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true &&
          document.documentElement.dataset.blendAnalytics === 'allowed' &&
          window[`ga-disable-${DESTINATION}`] !== true && typeof window.gtag === 'function';
      } catch (_) { return false; }
    }

    isVisible() { return document.visibilityState === 'visible' && !this.pageAway; }

    drawerVisible() {
      return Boolean(this.owner?.drawer?.open && !this.owner.drawerClosing && this.owner.drawerState !== 'closing');
    }

    tick() {
      const current = now();
      if (this.lastActiveAt !== null && this.lastActiveAt !== undefined) {
        const elapsed = Math.max(0, current - this.lastActiveAt);
        this.activeMs += elapsed;
        this.stepMs += elapsed;
        this.lastActiveAt = current;
      }
      [...this.pendingRequests.values(), this.iframeContext].filter(Boolean).forEach((pending) => {
        if (pending.journeyId !== this.journeyId) return;
        pending.parameters.active_time_ms = duration(this.activeMs);
        if (pending.viewKey === this.currentViewKey) pending.parameters.step_time_ms = duration(this.stepMs);
      });
    }

    stopClock() { this.tick(); this.lastActiveAt = null; }

    startClock() {
      if (this.active && this.drawerVisible() && this.isVisible() && this.lastActiveAt == null) this.lastActiveAt = now();
    }

    stepIndex(index) {
      return Number.isInteger(index) && index >= 0 && index < (this.owner?.steps?.length || 0) ? index : -1;
    }

    stageForStep(index) {
      const step = this.owner?.steps?.[this.stepIndex(index)];
      return safeToken(step?.dataset.measurementStep, STAGES,
        safeToken(QUESTION_STAGES[step?.dataset.questionKey], STAGES, 'other'));
    }

    state() {
      const route = safeToken(this.owner?.drawerRoute, ROUTES, 'start');
      if (route === 'start') return { route, stage: 'welcome', index: -1 };
      if (route === 'calendar') return {
        route,
        stage: this.owner?.bookingFrame ? safeToken(this.iframeStage, STAGES, 'booking_loading') :
          safeToken(BOOKING_STAGES[this.owner?.scheduler?.dataset.schedulerState], STAGES, 'booking_loading'),
        index: -1
      };
      if (this.owner?.confirmation && !this.owner.confirmation.hidden && !this.owner.form?.isConnected) {
        return { route, stage: 'enquiry_success', index: -1 };
      }
      const index = this.stepIndex(this.editIndex ?? this.owner?.inlineEdit?.stepIndex ?? this.owner?.currentStep);
      return { route, stage: this.stageForStep(index), index };
    }

    entry(trigger) {
      if (typeof trigger === 'string') return safeToken(trigger, ENTRIES, 'other');
      if (!trigger?.closest) return 'other';
      const explicit = trigger.closest('[data-contact-entry]')?.dataset.contactEntry;
      if (ENTRIES.has(explicit)) return explicit;
      if (trigger.closest('.agency-contact-landing')) return 'contact_page';
      if (trigger.closest('header, header-drawer, .shopify-section-header, [role="banner"]')) return 'header';
      if (trigger.closest('footer, .shopify-section-footer, [role="contentinfo"]')) return 'footer';
      return 'cta';
    }

    open(trigger) {
      if (this.destroyed || !this.drawerVisible() || this.active) return;
      this.qa = new URL(window.location.href).searchParams.get('contact_tracking_test') === '1';
      this.active = true;
      this.pageAway = false;
      this.hidden = document.visibilityState !== 'visible';
      this.cohort = this.isVisible() && this.canMeasure();
      this.entryType = this.entry(trigger);
      this.journeyId = randomId();
      this.activeMs = 0;
      this.stepMs = 0;
      this.lastActiveAt = null;
      this.currentViewKey = null;
      this.currentRoute = null;
      this.currentState = null;
      this.currentViewType = 'first';
      this.editIndex = null;
      this.formStarted = false;
      this.stepStarted = false;
      this.seenViews.clear();
      this.seenRoutes.clear();
      this.completed.clear();
      this.validationTimes.clear();
      const state = this.state();
      this.outcome = state.stage === 'booking_success' ? 'booking_complete' :
        state.stage === 'enquiry_success' ? 'enquiry_sent' : 'incomplete';
      this.startClock();
      this.event('contact_drawer_open', { action: 'open' });
      this.view('open');
    }

    view(reason = 'progress') {
      if (!this.active || !this.drawerVisible() || !this.isVisible()) return;
      const state = this.state();
      const key = `${state.route}:${state.stage}:${state.index}:${this.editIndex === null ? 'normal' : 'edit'}`;
      if (key === this.currentViewKey) return;
      this.tick();
      this.stepMs = 0;
      this.stepStarted = false;
      this.currentViewKey = key;
      this.currentState = state;
      const seenKey = `${state.route}:${state.stage}:${state.index}`;
      this.currentViewType = this.seenViews.has(seenKey) ? 'revisit' : 'first';
      this.seenViews.add(seenKey);
      if (state.route !== this.currentRoute) {
        const routeViewType = this.seenRoutes.has(state.route) ? 'revisit' : 'first';
        this.currentRoute = state.route;
        this.seenRoutes.add(state.route);
        this.event('contact_route_view', { action: reason, view_type: routeViewType });
      }
      if (state.route === 'form' && state.index >= 0) this.event('contact_step_view', { action: reason });
      else if (state.route === 'calendar') this.event('contact_booking_view', { action: reason });
      else if (state.stage === 'enquiry_success') this.event('contact_step_view', { action: reason });
    }

    parameters(detail = {}) {
      const state = this.state();
      const index = this.stepIndex(detail.index ?? detail.step_index ?? state.index);
      const stage = safeToken(detail.stage, STAGES,
        index >= 0 && (detail.index !== undefined || detail.step_index !== undefined) ? this.stageForStep(index) : state.stage);
      const route = safeToken(detail.route, ROUTES, state.route);
      return {
        contact_stage: stage,
        contact_route: route,
        contact_action: safeToken(detail.action, ACTIONS, 'other'),
        contact_entry: safeToken(this.entryType, ENTRIES, 'other'),
        contact_view_type: detail.view_type === 'first' || detail.view_type === 'revisit' ? detail.view_type : this.currentViewType || 'first',
        contact_outcome: safeToken(this.outcome, OUTCOMES, 'incomplete'),
        contact_version: VERSION,
        step_number: route === 'form' && index >= 0 ? index + 1 : 0,
        step_count: Math.max(0, Math.min(100, this.owner?.steps?.length || 0)),
        active_time_ms: duration(this.activeMs),
        step_time_ms: duration(index === state.index && stage === state.stage ? this.stepMs : 0)
      };
    }

    deliver(name, parameters, id, qa = this.qa) {
      if (!this.canMeasure()) return false;
      const event = qa ? `${name}_qa` : name;
      const payload = { ...parameters, event_id: `${id}:${++this.sequence}:${name}` };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...payload, measurement_collector: 'direct_gtag' });
      window.gtag('event', event, { send_to: DESTINATION, ...payload, ...(qa ? { debug_mode: true } : {}) });
      return true;
    }

    event(name, detail = {}) {
      if (!EVENTS.has(name) || name === 'contact_landing_view' || name === 'contact_landing_select') return false;
      const safeDetail = detail && typeof detail === 'object' ? detail : {};
      const success = name === 'contact_submit_success' || name === 'contact_booking_success';
      const terminalError = ['contact_submit_error', 'contact_booking_error'].includes(name) && TERMINAL_ERRORS.has(safeDetail.error_code);
      if (success || terminalError) return this.requestOutcome(name, safeDetail);
      if (!this.active || !this.drawerVisible() || (!LIFECYCLE.has(name) && !this.isVisible())) return false;
      this.tick();
      const kind = name === 'contact_submit_attempt' ? 'enquiry' : name === 'contact_booking_submit' ? 'booking' : null;
      let pending;
      if (kind) {
        if (this.pendingRequests.has(kind)) return false;
        const previousOutcome = this.outcome;
        this.outcome = `${kind}_pending`;
        pending = {
          journeyId: this.journeyId, qa: this.qa, cohort: false, viewKey: this.currentViewKey,
          previousOutcome, parameters: this.parameters(safeDetail)
        };
        this.pendingRequests.set(kind, pending);
        if (kind === 'enquiry') this.lastEnquirySuccessMeasured = false;
      }
      if (!this.cohort || !this.canMeasure()) return false;
      const parameters = this.parameters(safeDetail);
      if (['contact_validation_error', 'contact_submit_error', 'contact_booking_error', 'contact_open_error'].includes(name)) {
        parameters.contact_error_code = safeToken(safeDetail.error_code, ERRORS, 'unknown');
      }
      const delivered = this.deliver(name, parameters, this.journeyId);
      if (pending) pending.cohort = delivered;
      if (this.owner?.bookingFrame && delivered && parameters.contact_route === 'calendar' &&
          ['contact_booking_view', 'contact_booking_action'].includes(name)) {
        this.iframeContext = {
          journeyId: this.journeyId, qa: this.qa, cohort: true, viewKey: this.currentViewKey,
          parameters: { ...parameters }
        };
      }
      return delivered;
    }

    bookingFrameLoaded() {
      if (!['booking_profile', 'booking_date', 'booking_details', 'booking_success'].includes(this.iframeStage)) this.iframeStage = 'booking_loaded';
      this.view('progress');
    }

    bookingFrameError() {
      this.iframeStage = 'booking_error';
      this.view('progress');
      if (this.state().route === 'calendar') this.event('contact_booking_error', { error_code: 'unavailable' });
    }

    providerMessage(event) {
      if (this.destroyed) return;
      const frame = this.owner?.bookingFrame;
      if (!frame || event?.source !== frame.contentWindow) return;
      const origin = new URL(frame.dataset.bookingSrc || frame.src, window.location.href).origin;
      if (!['https://calendly.com', 'https://www.calendly.com'].includes(origin) || event.origin !== origin) return;
      const name = event.data?.event;
      if (!['calendly.profile_page_viewed', 'calendly.event_type_viewed', 'calendly.date_and_time_selected', 'calendly.event_scheduled'].includes(name)) return;
      if (name === 'calendly.event_scheduled') {
        this.providerSuccess(event.data?.payload);
        return;
      }
      if (this.providerCompleted && ['calendly.event_type_viewed', 'calendly.date_and_time_selected'].includes(name)) {
        this.providerCycle += 1;
        this.providerCompleted = false;
      }
      this.iframeStage = name === 'calendly.profile_page_viewed' ? 'booking_profile' :
        name === 'calendly.event_type_viewed' ? 'booking_date' : 'booking_details';
      // Cache hidden/prewarmed provider state, but never count it as an observed view.
      if (this.active && this.drawerVisible() && this.isVisible() && this.state().route === 'calendar') {
        this.view('progress');
        if (name === 'calendly.date_and_time_selected') this.event('contact_booking_action', { action: 'time_select' });
      }
    }

    providerSuccess(payload) {
      // Receipt identifiers are used only in memory for deduplication, never in event parameters.
      const eventUri = typeof payload?.event?.uri === 'string' ? payload.event.uri.slice(0, 2048) : '';
      const inviteeUri = typeof payload?.invitee?.uri === 'string' ? payload.invitee.uri.slice(0, 2048) : '';
      const receipt = eventUri || inviteeUri ? `${eventUri}|${inviteeUri}` : `cycle:${this.providerCycle}`;
      if (this.providerReceipts.has(receipt) || (this.providerCompleted && !eventUri && !inviteeUri)) return;
      this.providerReceipts.add(receipt);
      if (this.providerReceipts.size > 50) this.providerReceipts.delete(this.providerReceipts.values().next().value);
      this.providerCompleted = true;
      this.tick();
      this.iframeStage = 'booking_success';
      const context = this.iframeContext;
      this.iframeContext = null;
      if (context?.journeyId === this.journeyId) this.outcome = 'booking_complete';
      if (context?.cohort && this.canMeasure()) {
        this.deliver('contact_booking_success', {
          ...context.parameters, contact_stage: 'booking_success', contact_route: 'calendar',
          contact_action: 'success', contact_outcome: 'booking_complete', step_number: 0
        }, context.journeyId, context.qa);
      }
      this.view('success');
    }

    requestOutcome(name, detail) {
      const kind = name.startsWith('contact_submit_') ? 'enquiry' : 'booking';
      const pending = this.pendingRequests.get(kind);
      if (!pending) return false;
      this.tick();
      this.pendingRequests.delete(kind);
      const success = name.endsWith('_success');
      const code = safeToken(detail.error_code, ERRORS, 'unknown');
      const outcome = success ? (kind === 'enquiry' ? 'enquiry_sent' : 'booking_complete') :
        ['network', 'timeout', 'invalid_response'].includes(code) ? `${kind}_unknown` : pending.previousOutcome;
      if (pending.journeyId === this.journeyId) this.outcome = outcome;
      const parameters = {
        ...pending.parameters,
        contact_action: success ? 'success' : 'submit',
        contact_outcome: safeToken(outcome, OUTCOMES, 'incomplete'),
        ...(success ? {
          contact_stage: kind === 'enquiry' ? 'enquiry_success' : 'booking_success',
          step_number: 0
        } : { contact_error_code: code })
      };
      const delivered = pending.cohort && this.canMeasure() && this.deliver(name, parameters, pending.journeyId, pending.qa);
      if (kind === 'enquiry') this.lastEnquirySuccessMeasured = Boolean(success && delivered && !pending.qa);
      return delivered;
    }

    canTrackEnquirySuccess() {
      return this.lastEnquirySuccessMeasured === true && this.canMeasure();
    }

    agentEntry() {
      if (!this.active) return;
      this.entryType = 'agent';
      this.event('contact_navigation', { action: 'agent' });
    }

    completeStep(index) {
      index = this.stepIndex(index);
      if (!this.active || index < 0 || !this.drawerVisible() || !this.isVisible()) return;
      this.startFormStep(index, 'next');
      if (this.completed.has(index)) return;
      this.completed.add(index);
      this.event('contact_step_complete', { index, action: 'next', view_type: 'first' });
    }

    validation(field, index) {
      if (!this.active || !this.drawerVisible() || !this.isVisible()) return;
      const validity = field?.validity;
      const state = this.state();
      const validIndex = this.stepIndex(index);
      const stage = validIndex >= 0 ? this.stageForStep(validIndex) : state.stage;
      if (validIndex >= 0) this.startFormStep(validIndex, 'submit');
      let error = 'invalid_value';
      if (validity?.valueMissing) error = 'required';
      else if (field?.type === 'email' || stage === 'email') error = 'invalid_email';
      else if (field?.type === 'url' || stage === 'website') error = 'invalid_url';
      else if (field?.type === 'radio' || field?.type === 'checkbox') error = 'invalid_choice';
      const key = `${stage}:${error}`;
      const time = now();
      if (this.validationTimes.has(key) && time - this.validationTimes.get(key) < 500) return;
      this.validationTimes.set(key, time);
      this.event('contact_validation_error', { index: validIndex, stage, error_code: error, action: 'submit' });
    }

    fieldInteraction(event) {
      if (!this.active || !this.drawerVisible() || !this.isVisible() || !['input', 'change'].includes(event?.type)) return;
      const field = event.target;
      if (!field?.matches?.('input:not([type="hidden"]), textarea, select') || !this.owner?.contains?.(field)) return;
      if (this.state().route !== 'form') return;
      const step = field.closest('[data-form-step]');
      const index = Array.from(this.owner.steps || []).indexOf(step);
      if (index < 0 || index !== this.state().index) return;
      this.startFormStep(index, event.type);
    }

    startFormStep(index, action) {
      if (!this.active || !this.drawerVisible() || !this.isVisible() || this.state().route !== 'form') return;
      if (this.stepIndex(index) < 0 || index !== this.state().index) return;
      this.view('progress');
      if (!this.formStarted) { this.formStarted = true; this.event('contact_form_start', { action }); }
      if (!this.stepStarted) { this.stepStarted = true; this.event('contact_step_start', { action, index }); }
    }

    beginEdit(index) {
      index = this.stepIndex(index);
      if (!this.active || index < 0 || !this.drawerVisible() || !this.isVisible()) return;
      this.editIndex = index;
      this.view('edit');
      this.event('contact_step_edit', { index, action: 'edit' });
    }

    finishEdit(index, save) {
      index = this.stepIndex(index);
      if (!this.active || index < 0 || this.editIndex !== index) return;
      this.event(save ? 'contact_step_edit_save' : 'contact_step_edit_cancel', { index, action: save ? 'save' : 'cancel' });
      this.editIndex = null;
      this.view('return');
    }

    reset() {
      if (!this.active) return;
      this.event('contact_form_reset', { action: 'reset' });
      this.validationTimes.clear();
      this.editIndex = null;
      this.currentViewKey = null;
      this.stepStarted = false;
      // A reset is another pass within the same open journey, not a second form start.
    }

    close(reason = 'programmatic') {
      if (!this.active) return;
      this.event('contact_drawer_close', { action: reason });
      this.stopClock();
      this.active = false;
      this.cohort = false;
    }

    visibilityChanged() {
      if (this.destroyed) return;
      this.recordLandingView();
      if (!this.active) return;
      if (document.visibilityState !== 'visible') {
        if (this.hidden) return;
        this.stopClock();
        this.hidden = true;
        this.event('contact_drawer_hidden', { action: 'hidden' });
      } else if (this.hidden && !this.pageAway) {
        this.hidden = false;
        this.startClock();
        this.event('contact_drawer_return', { action: 'visible' });
        this.view('return');
      }
    }

    pageHidden(event) {
      if (!this.active) return;
      this.stopClock();
      this.event('contact_drawer_page_exit', { action: event?.persisted ? 'bfcache' : 'navigation' });
      this.pageAway = true;
      this.hidden = true;
      if (!event?.persisted) { this.active = false; this.cohort = false; }
    }

    pageShown(event) {
      if (!this.active || !this.pageAway || !event?.persisted) return;
      this.pageAway = false;
      if (document.visibilityState === 'visible') {
        this.hidden = false;
        this.startClock();
        this.event('contact_drawer_return', { action: 'bfcache' });
        this.view('return');
      }
    }

    setupLanding() {
      if (this.destroyed || this.landingObserver || !/^\/pages\/contact-us\/?$/.test(window.location.pathname)) return;
      const landing = document.querySelector('.agency-contact-landing__actions');
      if (!landing || typeof IntersectionObserver === 'undefined') return;
      this.landingObserver = new IntersectionObserver((entries) => {
        if (this.destroyed) return;
        this.landingVisible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.25);
        this.recordLandingView();
      }, { threshold: 0.25 });
      this.landingObserver.observe(landing);
      if (typeof MutationObserver !== 'undefined') {
        this.consentObserver = new MutationObserver(() => this.recordLandingView());
        this.consentObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-blend-analytics'] });
      }
    }

    landingParameters(route, action) {
      return {
        contact_stage: 'welcome', contact_route: route, contact_action: action,
        contact_entry: 'contact_page', contact_view_type: 'first', contact_outcome: 'incomplete',
        contact_version: VERSION, step_number: 0, step_count: Math.max(0, Math.min(100, this.owner?.steps?.length || 0)),
        active_time_ms: 0, step_time_ms: 0
      };
    }

    recordLandingView() {
      if (this.destroyed || this.landingCohort || !this.landingVisible || document.visibilityState !== 'visible' || !this.canMeasure()) return;
      this.landingCohort = this.deliver('contact_landing_view', this.landingParameters('start', 'open'), this.landingId);
    }

    landingSelect(route) {
      if (!['form', 'calendar'].includes(route) || !this.landingVisible || document.visibilityState !== 'visible') return;
      this.recordLandingView();
      if (!this.landingCohort || !this.canMeasure()) return;
      const parameters = this.landingParameters(route, 'route_select');
      parameters.contact_view_type = this.landingSelections.has(route) ? 'revisit' : 'first';
      if (this.deliver('contact_landing_select', parameters, this.landingId)) this.landingSelections.add(route);
    }

    destroy() {
      if (this.destroyed) return;
      this.stopClock();
      this.active = false;
      this.cohort = false;
      this.destroyed = true;
      this.landingObserver?.disconnect();
      this.consentObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('visitorConsentCollected', this.onConsent);
      document.removeEventListener('DOMContentLoaded', this.onDomReady);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('message', this.onProviderMessage);
    }
  }

  // DOM/provider/consent failures are never allowed to block contact or booking.
  Object.getOwnPropertyNames(BlendContactMeasurement.prototype).forEach((name) => {
    if (name === 'constructor') return;
    const method = BlendContactMeasurement.prototype[name];
    BlendContactMeasurement.prototype[name] = function (...args) {
      try { return method.apply(this, args); } catch (_) { return false; }
    };
  });
  window.BlendContactMeasurement = BlendContactMeasurement;
})();
